export const DAILY_CHECK_IN_CREDIT_REWARDS = [3, 3, 6, 3, 3, 3, 9] as const;
export const DAILY_CHECK_IN_CREDIT_TOTAL = 30;
export const DAILY_CHECK_IN_JOURNEY_DAYS = 13;

export type DailyCheckInCreativeRewardKey =
  | 'cinematic_product'
  | 'editorial_portrait'
  | 'motion_sequence'
  | 'social_ad'
  | 'visual_story';

export interface DailyCheckInCreativeReward {
  day: number;
  key: DailyCheckInCreativeRewardKey;
  title: string;
  description: string;
  prompt: string;
}

export const DAILY_CHECK_IN_CREATIVE_REWARDS: readonly DailyCheckInCreativeReward[] =
  [
    {
      day: 8,
      key: 'cinematic_product',
      title: 'Cinematic product spotlight',
      description: 'A polished prompt for premium product imagery.',
      prompt:
        'A cinematic product hero shot on a sculpted pedestal, precise rim lighting, controlled reflections, subtle atmospheric depth, premium campaign art direction, crisp material detail, balanced negative space, photorealistic, 4K',
    },
    {
      day: 9,
      key: 'editorial_portrait',
      title: 'Editorial character portrait',
      description:
        'A magazine-style portrait prompt with strong art direction.',
      prompt:
        'Editorial character portrait, confident natural pose, expressive eyes, directional studio lighting, textured wardrobe, refined color separation, contemporary magazine photography, authentic skin detail, 85mm lens look, shallow depth of field',
    },
    {
      day: 10,
      key: 'motion_sequence',
      title: 'Dynamic motion sequence',
      description: 'A reusable direction prompt for image-to-video movement.',
      prompt:
        'Slow cinematic camera push-in with subtle parallax, natural secondary motion in hair and fabric, realistic light changes, stable subject identity, smooth acceleration and deceleration, no warping, no sudden cuts',
    },
    {
      day: 11,
      key: 'social_ad',
      title: 'High-converting social ad',
      description: 'A clean commercial visual prompt for social campaigns.',
      prompt:
        'High-impact social campaign visual, one unmistakable focal point, product benefit communicated through the scene, bold but restrained color contrast, clean composition for mobile, generous copy space, commercial photography, premium finish',
    },
    {
      day: 12,
      key: 'visual_story',
      title: 'Three-shot visual story',
      description: 'A prompt framework for a coherent three-scene sequence.',
      prompt:
        'Create a coherent three-shot visual story: wide establishing shot, intimate medium action shot, memorable close-up resolution; preserve character, wardrobe, lighting direction, palette, and environment continuity across every scene',
    },
  ];

export type DailyCheckInReward =
  | { type: 'credit'; credits: number }
  | { type: 'creative_prompt'; reward: DailyCheckInCreativeReward }
  | { type: 'complete' };

export function getDailyCheckInReward(day: number): DailyCheckInReward {
  if (!Number.isInteger(day) || day < 1 || day > DAILY_CHECK_IN_JOURNEY_DAYS) {
    throw new RangeError(`Invalid daily check-in day: ${day}`);
  }

  if (day <= DAILY_CHECK_IN_CREDIT_REWARDS.length) {
    return {
      type: 'credit',
      credits: DAILY_CHECK_IN_CREDIT_REWARDS[day - 1],
    };
  }

  const creativeReward = DAILY_CHECK_IN_CREATIVE_REWARDS.find(
    (reward) => reward.day === day
  );
  if (creativeReward) {
    return { type: 'creative_prompt', reward: creativeReward };
  }

  return { type: 'complete' };
}

export function getUtcDateKey(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function getNextUtcDay(date: Date): Date {
  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + 1)
  );
}
